import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { Logo } from "./Logo";
import { scrollTo } from "@/lib/lenis";

const LINKS = [
  { label: "Abonamenty", to: "#abonamenty" },
  { label: "Dla nauczycielek", to: "#nauczycielki" },
  { label: "Dla właścicielek", to: "#wlascicielki" },
  { label: "Chcę mieć wszystko", to: "#mam-wszystko" },
  { label: "Co dostajesz", to: "#co-dostajesz" },
  { label: "FAQ", to: "#faq" },
];

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (to) => (e) => {
    e.preventDefault();
    setOpen(false);
    scrollTo(to);
  };

  return (
    <header
      data-testid="site-nav"
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,box-shadow] duration-500 ${
        scrolled || open ? "bg-alabaster/95 border-b border-ink/10 backdrop-blur-sm" : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="mx-auto flex h-[72px] max-w-[1440px] items-center justify-between px-5 sm:px-8 lg:px-12">
        <Logo />
        <nav className="hidden items-center gap-7 lg:flex" aria-label="Nawigacja główna">
          {LINKS.map((l) => (
            <a
              key={l.to}
              href={l.to}
              onClick={go(l.to)}
              data-testid={`nav-link-${l.to.slice(1)}`}
              className="nav-link text-[13px] font-medium text-ink/75 hover:text-ink"
            >
              {l.label}
            </a>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          <a
            href="#abonamenty"
            onClick={go("#abonamenty")}
            data-testid="nav-wybieram-abonament-button"
            className="hidden items-center bg-ink px-5 py-2.5 text-[12px] font-semibold uppercase tracking-[0.14em] text-white transition-colors duration-300 hover:bg-red sm:inline-flex"
          >
            Wybieram abonament
          </a>
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-label={open ? "Zamknij menu" : "Otwórz menu"}
            aria-expanded={open}
            data-testid="nav-mobile-toggle"
            className="inline-flex h-10 w-10 items-center justify-center border border-ink/20 text-ink lg:hidden"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>
      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="nav-mobile-menu"
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.25 }}
            className="border-t border-ink/10 bg-alabaster px-5 pb-8 pt-4 lg:hidden"
            aria-label="Nawigacja mobilna"
          >
            {LINKS.map((l, i) => (
              <a
                key={l.to}
                href={l.to}
                onClick={go(l.to)}
                data-testid={`nav-mobile-link-${l.to.slice(1)}`}
                className="flex items-center justify-between border-b border-ink/10 py-4 font-display text-2xl text-ink"
              >
                {l.label}
                <span className="text-xs font-sans text-ink/40">0{i + 1}</span>
              </a>
            ))}
            <a
              href="#abonamenty"
              onClick={go("#abonamenty")}
              data-testid="nav-mobile-wybieram-abonament-button"
              className="mt-6 flex items-center justify-center bg-red px-5 py-4 text-[13px] font-semibold uppercase tracking-[0.14em] text-white"
            >
              Wybieram abonament
            </a>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
